// What the heartbeat says about a tasting in progress, worked out from the run on this device. A
// solo tasting is only "solo": no wine, title, code or session leaves the device. A shared one
// names its session (or flight code, which the edge only uses to find the flight) and the guided
// page the person is on.
import type { PresenceActivityReport } from "@proef/contract";
import { ACTIVITY_MAX_MS } from "@proef/domain";
import { FULL_STEPS, type TastingRun } from "../tasting/run";

/** Whether the run counts as a tasting under way: not left, and begun. The lobby, shared or not,
 * is waiting, not tasting. */
function isTasting(run: TastingRun): boolean {
  return !run.finished && run.started;
}

/** The guided page of the wine in front of the taster; a quick tasting has no pages. */
function stepOf(run: TastingRun): PresenceActivityReport["step"] {
  if (run.depth === "quick") return undefined;
  const index = run.wines[run.current]?.step;
  return index === undefined ? undefined : FULL_STEPS[index];
}

/**
 * The activity to report for `run`, or null when there is none to show. `firstSeen` stands in for
 * the start of a run an older build stored without one; a run past the 4 h cap reports nothing, so a
 * tasting left open on a phone never reads as "tasting now" for days.
 */
export function activityOfRun(
  run: TastingRun | undefined,
  nowMs: number,
  firstSeen: string,
): PresenceActivityReport | null {
  if (run === undefined || !isTasting(run)) return null;
  const startedAt = run.startedAt ?? firstSeen;
  if (nowMs - Date.parse(startedAt) > ACTIVITY_MAX_MS) return null;
  if (run.sessionId !== undefined) {
    const step = stepOf(run);
    return {
      kind: "session",
      sessionId: run.sessionId,
      startedAt,
      ...(step === undefined ? {} : { step }),
    };
  }
  if (run.flightCode !== undefined) {
    const step = stepOf(run);
    return {
      kind: "flight",
      flightCode: run.flightCode,
      startedAt,
      ...(step === undefined ? {} : { step }),
    };
  }
  return { kind: "solo", startedAt };
}

/** What makes two reports the same tasting: a change of it is a start or a finish worth reporting
 * at once, while the guided page only rides along on the next heartbeat. */
export function activityKey(activity: PresenceActivityReport | null): string {
  if (activity === null) return "";
  return [activity.kind, activity.sessionId ?? "", activity.flightCode ?? ""].join("|");
}
